/**
 * Next-Best-Action engine for privacy chain investigation nodes.
 *
 * Given a node's current data, suggests the most valuable next investigative
 * step. Reads what evidence the node already has and what's missing.
 *
 * Architecture: Frontend utility — reads from node.data and expanded_transactions.
 * No backend call needed. Returns prioritized suggestions.
 */

export interface NextAction {
    /** Unique action identifier */
    id: string;
    /** Priority 1-5 (1 = highest) */
    priority: number;
    /** Human-readable action */
    action: string;
    /** Why this is suggested */
    reason: string;
    /** What evidence category this produces */
    evidenceType: 'crypto_proof' | 'service_assertion' | 'chain_fact' | 'endpoint_artifact' | 'heuristic';
    /** Which tool/panel to use */
    tool?: string;
}

const PRIVACY_CHAINS = new Set(['XMR', 'ZEC', 'DASH', 'ARRR', 'FIRO']);

/**
 * Compute next-best-action suggestions for a privacy chain node.
 */
export function computeNextActions(nodeData: Record<string, any>): NextAction[] {
    const chain = (nodeData.chain || '').toUpperCase();
    if (!PRIVACY_CHAINS.has(chain)) return [];

    const actions: NextAction[] = [];
    const nodeType = nodeData.type || 'address';
    const hasKeys = Array.isArray(nodeData._privacyKeys) && nodeData._privacyKeys.length > 0;
    const hasSubpoenas = nodeData._subpoenaStatuses && Object.values(nodeData._subpoenaStatuses).some((s: any) => s?.status === 'submitted' || s?.status === 'received');
    const hasExpandedTxs = Array.isArray(nodeData.expanded_transactions) && nodeData.expanded_transactions.length > 0;
    const hasEntityName = !!nodeData.entity_name && nodeData.entity_name !== 'Unknown';
    const hasTriage = nodeData._triageCompleted || nodeData.risk_score != null;
    const hasInvestigation = nodeData._investigationCompleted;
    const isUnshielding = nodeData.is_unshielding;
    const timestamp = nodeData.timestamp;

    // ── XMR-specific actions ──────────────────────────────────────────
    if (chain === 'XMR') {
        if (!hasKeys) {
            actions.push({
                id: 'xmr_tx_private_key',
                priority: 1,
                action: 'Request TX private key from sender/exchange',
                reason: 'TX private key is the strongest evidence for XMR — proves sender and amount. Without it, this TX is a metadata-only lead.',
                evidenceType: 'crypto_proof',
                tool: 'View Key / TX Private Key input (OverviewTab)',
            });
        }
        if (!hasSubpoenas) {
            actions.push({
                id: 'xmr_subpoena_swap',
                priority: 1,
                action: 'Subpoena ChangeNow / FixedFloat for IP + order records',
                reason: 'No-KYC swap services are the primary XMR on/off-ramp. Subpoena for IP logs + deposit/withdrawal ±2hr of TX timestamp.',
                evidenceType: 'service_assertion',
                tool: 'Subpoena Targets panel (OverviewTab)',
            });
        }
        if (timestamp) {
            actions.push({
                id: 'xmr_cross_chain_timing',
                priority: 2,
                action: 'Search for BTC/USDT transactions within ±30min of this TX timestamp',
                reason: 'Cross-chain timing correlation: XMR purchases are often preceded by BTC/USDT sales on exchanges within a narrow time window.',
                evidenceType: 'heuristic',
                tool: 'Add transparent chain addresses to canvas, run Temporal Correlation',
            });
        }
        actions.push({
            id: 'xmr_device_forensics',
            priority: 3,
            action: 'Check seized device for Monero wallet files',
            reason: 'Wallet files at ~/.bitmonero/ (Linux), AppData\\Roaming\\bitmonero (Win), or Cake Wallet / Feather paths. Contains address book, TX history, view keys.',
            evidenceType: 'endpoint_artifact',
        });
    }

    // ── ZEC-specific actions ──────────────────────────────────────────
    if (chain === 'ZEC') {
        if (isUnshielding) {
            actions.push({
                id: 'zec_trace_exit',
                priority: 1,
                action: 'Trace the receiving transparent address (Z→T exit point)',
                reason: `This TX exits the shielded pool${nodeData.exit_amount ? ` with ${Number(nodeData.exit_amount).toFixed(4)} ZEC` : ''}. The receiving t-address is now fully traceable — add it to canvas and trace like BTC.`,
                evidenceType: 'chain_fact',
                tool: 'Add t-address to canvas → Trace',
            });
        }
        if (!hasKeys) {
            actions.push({
                id: 'zec_viewing_key',
                priority: 1,
                action: 'Court order exchange for viewing key disclosure',
                reason: 'Exchanges holding ZEC must retain viewing keys for shielded addresses. Court order reveals all incoming transaction amounts.',
                evidenceType: 'crypto_proof',
                tool: 'View Key input (OverviewTab)',
            });
        }
        if (nodeData.is_transparent) {
            actions.push({
                id: 'zec_trace_transparent',
                priority: 2,
                action: 'Trace this transparent address like Bitcoin',
                reason: 'ZEC transparent addresses (t1/t3) are fully visible. Standard graph tracing applies.',
                evidenceType: 'chain_fact',
                tool: 'Trace button',
            });
        }
    }

    // ── DASH-specific actions ─────────────────────────────────────────
    if (chain === 'DASH') {
        if (nodeData.is_coinjoin) {
            const md = nodeData.mixing_depth;
            const rounds = md?.estimated_rounds || '?';
            const addr = md?.addressability_pct || '?';
            actions.push({
                id: 'dash_coinjoin_analysis',
                priority: 2,
                action: 'Analyze CoinJoin mixing chain depth',
                reason: `This TX is a PrivateSend CoinJoin (${rounds} round, ~${addr}% addressable). Trace input UTXOs backward — each CoinJoin adds a round. Fewer rounds = higher traceability.`,
                evidenceType: 'heuristic',
                tool: 'Expand node → check for more CoinJoin TXs in history',
            });
        } else {
            actions.push({
                id: 'dash_standard_trace',
                priority: 2,
                action: 'Trace normally — this is a standard DASH TX (not PrivateSend)',
                reason: 'Non-PrivateSend DASH transactions are fully transparent like Bitcoin.',
                evidenceType: 'chain_fact',
                tool: 'Trace button',
            });
        }
    }

    // ── Universal privacy chain actions ───────────────────────────────
    if (!hasEntityName) {
        actions.push({
            id: 'entity_lookup',
            priority: 2,
            action: 'Run entity lookup against intelligence database',
            reason: 'No entity attribution found yet. Check if this address is in our 1.27M entity database.',
            evidenceType: 'service_assertion',
            tool: 'Intel tab → loads automatically',
        });
    }

    if (!hasExpandedTxs && nodeType === 'address') {
        actions.push({
            id: 'load_transactions',
            priority: 2,
            action: 'Load transactions for this address',
            reason: 'No transactions loaded yet. Expanding will reveal counterparties and timing patterns.',
            evidenceType: 'chain_fact',
            tool: 'Transfers tab → Load transactions',
        });
    }

    if (!hasInvestigation && hasTriage) {
        actions.push({
            id: 'run_investigation',
            priority: 3,
            action: 'Run Auto-Investigation (8-step pipeline)',
            reason: 'Full investigation pipeline will analyze velocity, counterparties, exposure, and generate an LE report with privacy chain section.',
            evidenceType: 'heuristic',
            tool: 'Analysis tab → Investigate',
        });
    }

    // ── OSINT-aware actions ──────────────────────────────────────────
    // Check if identity/OSINT data has been loaded (IntelligenceTab sets these on the node)
    const identityData = nodeData._identity || nodeData._identityResult;
    const hasIdentityHits = identityData && (
        (identityData.linked_contacts?.length > 0) ||
        (identityData.identity_results?.length > 0) ||
        (identityData.web_mentions && Object.keys(identityData.web_mentions).length > 0)
    );

    if (hasIdentityHits) {
        // We have OSINT hits — suggest following up
        const contactCount = identityData.linked_contacts?.length || 0;
        actions.push({
            id: 'osint_followup',
            priority: 1,
            action: `Follow up on ${contactCount} OSINT lead${contactCount !== 1 ? 's' : ''} found`,
            reason: `Identity search found linked contacts (phones/UPIs/emails from Telegram/P2P channels). Cross-reference with suspect's known identifiers.`,
            evidenceType: 'service_assertion',
            tool: 'Intel tab → Identity & OSINT section',
        });
    } else if (!identityData) {
        // No identity data loaded yet — suggest checking
        actions.push({
            id: 'osint_check',
            priority: 2,
            action: 'Check Telegram/P2P intelligence for this address',
            reason: `Address may appear in scraped Telegram P2P channels (22 Indian crypto channels monitored) or exchange P2P ads. Expand Intel tab to check.`,
            evidenceType: 'service_assertion',
            tool: 'Intel tab → Identity & OSINT (expand to load)',
        });
    }

    // Sort by priority
    actions.sort((a, b) => a.priority - b.priority);
    return actions;
}
